import { Button, Divider } from '@heroui/react';
import { Link } from 'react-router-dom';
import {
  ASSIGNMENT_SUBMISSION_STATUS_LABELS,
  AssignmentSubmissionStatusEnum,
  IAssignmentNormalizedItem,
} from '@/entities/Assignment';
import { AttachmentsDownloadList } from '@/shared/ui/AttachmentsDownloadList';
import { Section } from '@/shared/ui/Section';
import { UserAvatar } from '@/shared/ui/UserAvatar';
import { getColorClass } from '@/shared/lib/utils/getColorClass';

interface IAssignmentItemProps {
  item: IAssignmentNormalizedItem;
}

export const AssignmentItem = ({ item }: IAssignmentItemProps) => {
  const {
    module,
    lesson,
    assignment,
    moduleTeacher,
    lecturer,
    buttonData,
    grade,
    assignmentStatus,
    submissionSubmittedAt,
    retake,
    turnitinData,

  } = item;
  const isSummative = lesson.type !== 'regular';
  const submissionStatus = assignmentStatus
    ? ASSIGNMENT_SUBMISSION_STATUS_LABELS[assignmentStatus]
    : null;

  const getStatusColorClass = () => {
    if (!assignmentStatus) return 'text-black';
    switch (assignmentStatus) {
      case AssignmentSubmissionStatusEnum.Completed:
        return 'text-green-secondary';
      case AssignmentSubmissionStatusEnum.Failed:
        return 'text-red-warning';
      case AssignmentSubmissionStatusEnum.AwaitingReview:
        return 'text-yellow-tertiary';
      default:
        return 'text-black';
    }
  };

  const getTurnitinColorClass = (percentage: number): string => {
    if (percentage < 10) {
      return 'text-green-secondary';
    }
    if (percentage >= 15 && percentage < 30) {
      return 'text-yellow-tertiary';
    }
    return 'text-red-tertiary';
  };

  return (
    <Section className="flex flex-col gap-y-4">
      <div className="inline-flex justify-between">
        <div>
          <h2 className="text-[24px] leading-[120%] tracking-0 font-medium">
            Lesson {lesson.order}: {lesson.title}
          </h2>
          <h3 className="text-[24px] leading-[120%] tracking-0 font-medium">
            Module: {module.title}
          </h3>
        </div>
        {isSummative && (
          <div className="flex items-center gap-x-4">
            <span className="bg-[#FFF0EE] py-1 px-5 rounded-full text-red-tertiary font-bold">
              {lesson.type.charAt(0).toUpperCase() + lesson.type.slice(1)}
            </span>
            {Boolean(grade || turnitinData) && (
              <div className="flex flex-col">
                {typeof grade === 'number' && (
                  <div className="flex justify-between gap-x-3 text-grey-secondary font-medium">
                    <p className="min-w-[73px]">Grade:</p>
                    <p className={getColorClass(grade)}>{grade}%</p>
                  </div>
                )}
                {typeof turnitinData?.matchesPercentage === 'number' && (
                  <div className="flex justify-between gap-x-3 text-grey-secondary font-medium">
                    <p className="min-w-[73px]">Similarity:</p>
                    <p className={getTurnitinColorClass(turnitinData.matchesPercentage)}>
                      {turnitinData.matchesPercentage}%
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <ul className="inline-flex gap-x-[30px] text-sm">
        {submissionSubmittedAt && (
          <li className="text-grey-secondary font-medium">
            Submitted: <span className="text-black">{submissionSubmittedAt}</span>
          </li>
        )}
        <li className="text-grey-secondary font-medium [&:not(:first-child)]:list-disc [&:not(:first-child)]:marker:text-black">
          Retake: <span className="text-black">{retake}</span>
        </li>
        {lesson.deadline && (
          <li className="text-grey-secondary font-medium [&:not(:first-child)]:list-disc [&:not(:first-child)]:marker:text-black">
            Deadline: <span className="text-black">{lesson.deadline}</span>
          </li>
        )}
      </ul>

      {submissionStatus && (
        <p className="text-grey-secondary font-medium text-sm">
          Submission status:{' '}
          <span
            className={`${getStatusColorClass()} text-[20px] leading-[120%] tracking-0 font-medium`}
          >
            {submissionStatus}
          </span>
        </p>
      )}

      <Divider as="div" className="h-[2px] bg-neutral-hover" />
      <div className="flex flex-col gap-y-2 text-black-headline">
        <p className="font-medium">Assignment: {assignment.title}</p>
        {assignment.description && (
          <p className="text-sm leading-[120%] tracking-0">{assignment.description}</p>
        )}
      </div>
      <AttachmentsDownloadList attachments={assignment.attachments ?? []} />
      <Divider as="div" className="h-[2px] bg-neutral-hover" />
      {(moduleTeacher || lecturer) && (
        <>
          <div className="flex items-center gap-x-4">
            {moduleTeacher && (
              <UserAvatar
                avatar={moduleTeacher.avatar}
                userName={`${moduleTeacher.firstName} ${moduleTeacher.lastName}`}
                href={`/staff/${moduleTeacher.id}`}
              />
            )}
            {lecturer && (
              <UserAvatar
                avatar={lecturer.avatar}
                userName={`${lecturer.firstName} ${lecturer.lastName}`}
                href={`/staff/${lecturer.id}`}
              />
            )}
          </div>
          <Divider as="div" className="h-[2px] bg-neutral-hover" />
        </>
      )}
      {buttonData ? (
        <Button
          as={Link}
          to={buttonData.href}
          className="self-start bg-[#211D55] text-white"
          radius="full"
        >
          {buttonData.text}
        </Button>
      ) : null}
    </Section>
  );
};
